import { Component, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { Subscription } from 'rxjs';

import { LocalDataSizePipe } from '../../shared/directives/local-data-size/local-data-size.pipe';
import { InstrumentationData } from '../../shared/instrumentation/instrumentation-data.model';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownAreas, KnownInstrumentationActions, KnownInstrumentationEvents } from '../../shared/instrumentation/instrumentation.service';
import { DocumentService } from '../../shared/services/document.service';
import { CapabilitiesService } from '../../shared/shared-context/capabilities.service';
import { LocService } from '../../shared/shared-context/loc.service';
import { KeyboardKey, KeystrokeHandler } from '../../shared/utilities/keystroke-handler.utility';
import { UploadErrorType, UploadSessionBase, UploadSessionProgress, UploadSessionState, UploadSource } from '../models/upload-session.model';
import { AppSpecificUploadResources, UploadConfigService } from '../upload-config.service';
import { UploadManagerService } from '../upload-manager.service';

import './upload-dialog.component.css';

@Component({
    selector: 'ohp-upload-dialog',
    templateUrl: './upload-dialog.component.html'
})
export class UploadDialogComponent implements OnInit, OnDestroy {

    public cancelButtonText: string;
    public cancelButtonAriaLabel: string;
    public uploadSessionState: UploadSessionBase;
    public uploadProgressTitle: string;
    public uploadCompleteTitle: string;
    public uploadCompleteSubtitle: string;
    public openWarning: string;
    public show: boolean;
    public progressText: string;
    public cancelEnabled: boolean;
    public cancelClickData: InstrumentationData;
    public showCompleted: boolean;
    public uploadPercentage: number;
    public dragAndDropTip: string;
    public showTip: boolean;

    private uploadResourcesSubscription: Subscription;
    private uploadProgressSubscription: Subscription;
    private uploadSessionSubscription: Subscription;
    private lastKnownState: UploadSessionBase;
    private keyListener: Function;

    constructor(
        private readonly capabilitiesService: CapabilitiesService,
        private readonly instrumentationService: InstrumentationService,
        private readonly localDataSizePipe: LocalDataSizePipe,
        private readonly locService: LocService,
        private readonly uploadConfigService: UploadConfigService,
        private readonly uploadManagerService: UploadManagerService,
        private readonly renderer: Renderer2,
        private readonly documentService: DocumentService
    ) {
    }

    public ngOnInit() {
        if (!this.uploadConfigService.config) {
            return;
        }
        const resources = this.uploadConfigService.config.commonResources;
        this.uploadCompleteSubtitle = resources.completedSubtitle;
        this.uploadCompleteTitle = resources.completedTitle;
        this.showCompleted = false;
        this.uploadPercentage = 0;
        this.uploadResourcesSubscription = this.uploadManagerService.uploadResources.subscribe(uploadResources => this.updateUploadResources(uploadResources));
        this.uploadSessionSubscription = this.uploadManagerService.uploadSession.subscribe(session => {
            this.updateDialog(session);
        });

        const handler = (event: KeyboardEvent) => {
            const key = KeystrokeHandler.getKey(event);
            if (this.show && key === KeyboardKey.Escape) {
                this.cancel();
            }
        };

        this.keyListener = this.renderer.listen(this.documentService.nativeDocument, 'keyup', handler);
    }

    public ngOnDestroy() {
        if (this.uploadResourcesSubscription) {
            this.uploadResourcesSubscription.unsubscribe();
        }
        if (this.uploadSessionSubscription) {
            this.uploadSessionSubscription.unsubscribe();
        }
        if (this.uploadProgressSubscription) {
            this.uploadProgressSubscription.unsubscribe();
        }

        if (this.keyListener) {
            this.keyListener();
        }
    }

    public cancel() {
        if (!this.cancelEnabled) {
            return;
        }
        this.uploadManagerService.cancelUpload();
    }

    private updateDialog(session: UploadSessionBase) {
        const resources = this.uploadConfigService.config.commonResources;
        this.uploadSessionState = session;
        this.showCompleted = !!session && session.state === UploadSessionState.Completed && (this.capabilitiesService.capabilities.pwaEnabled || this.capabilitiesService.capabilities.isEmbedMode);
        this.cancelButtonText = this.showCompleted || (!!session && session.state === UploadSessionState.Failed) ? resources.closeButtonTitle : resources.cancelButtonTitle;
        this.cancelButtonAriaLabel = this.showCompleted ? resources.closeButtonAriaLabel : resources.cancelButtonAriaLabel;
        this.show = !!session && (session.state === UploadSessionState.Started || session.state === UploadSessionState.Failed || session.state === UploadSessionState.Completed);
        this.cancelEnabled = !!this.uploadSessionState && (this.uploadSessionState.state !== UploadSessionState.Completed || this.showCompleted);
        this.cancelClickData = this.getCancelClickData();
        this.showTip = !!session && session.uploadSource === UploadSource.Button && this.uploadConfigService.config.dragAndDropEnabled;
        this.updateProgress();
        this.checkAndInstrumentImpression();
        this.lastKnownState = session;
    }

    private updateProgress() {
        // If the upload session hasn't changed, as when the state is updated, we keep current subscription
        if (this.uploadSessionState && this.lastKnownState && this.uploadSessionState === this.lastKnownState) {
            return;
        }

        // If we didn't have a session before or if this is a new or null session, reset the subscription
        this.resetProgress();
    }

    private resetProgress() {
        if (this.uploadProgressSubscription) {
            this.uploadProgressSubscription.unsubscribe();
        }

        if (!this.uploadSessionState) {
            this.progressText = '';
            this.uploadPercentage = 0;
            this.uploadProgressSubscription = null;
            return;
        }

        this.uploadProgressSubscription = this.uploadSessionState.getProgress().subscribe(progress => {
            this.progressText = this.getProgressText(progress);
            this.uploadPercentage = progress.percentage;
        });
    }

    private getCancelClickData(): InstrumentationData {
        if (!this.cancelEnabled) {
            return null;
        }

        const action = this.uploadSessionState.finished() ? KnownInstrumentationActions.Close : KnownInstrumentationActions.Cancel;
        const elementId = 'upload-cancel';
        const params: InstrumentationParams = {};
        params[InstrumentationParam.UploadSessionId] = this.uploadSessionState.sessionId;
        params[InstrumentationParam.ElementId] = elementId;
        params[InstrumentationParam.Action] = action;
        params[InstrumentationParam.Area] = KnownAreas.UploadDialog;
        params[InstrumentationParam.Latency] = Math.ceil(this.uploadSessionState.duration).toString();
        params[InstrumentationParam.ItemCategory] = UploadSource[this.uploadSessionState.uploadSource];

        return {
            eventName: KnownInstrumentationEvents.UploadClick,
            params,
            metrics: []
        };
    }

    private getProgressText(progress: UploadSessionProgress): string {
        const loadedString = this.localDataSizePipe.transform(progress.loaded);
        const totalString = this.localDataSizePipe.transform(progress.total);
        return this.locService.format(this.uploadConfigService.config.commonResources.progressText, loadedString, totalString);
    }

    private updateUploadResources(resources: AppSpecificUploadResources) {
        if (resources) {
            this.uploadProgressTitle = resources.progressTitle;
            this.openWarning = resources.openWarning;
            this.dragAndDropTip = resources.dragAndDropTip;
        }
    }

    private checkAndInstrumentImpression() {
        if (!this.uploadSessionState || this.lastKnownState && this.uploadSessionState.state === this.lastKnownState.state) {
            return;
        }
        if (this.uploadSessionState.state === UploadSessionState.Started) {
            const params: InstrumentationParams = {};
            params[InstrumentationParam.UploadSessionId] = this.uploadSessionState.sessionId;
            params[InstrumentationParam.ImpressionId] = 'progress';
            params[InstrumentationParam.ItemCategory] = UploadSource[this.uploadSessionState.uploadSource];
            this.instrumentationService.logImpressionEvent(KnownInstrumentationEvents.UploadDialogImpression, params);
        } else if (this.uploadSessionState.state === UploadSessionState.Failed) {
            const error = this.uploadSessionState.error;
            const params: InstrumentationParams = {};
            params[InstrumentationParam.UploadSessionId] = this.uploadSessionState.sessionId;
            params[InstrumentationParam.Error] = UploadErrorType[(error && error.type) ? error.type : UploadErrorType.Unknown];
            params[InstrumentationParam.ItemCount] = ((error && error.recourseAction) ? 2 : 1).toString();
            params[InstrumentationParam.ImpressionId] = 'error';
            params[InstrumentationParam.ItemCategory] = UploadSource[this.uploadSessionState.uploadSource];
            this.instrumentationService.logImpressionEvent(KnownInstrumentationEvents.UploadDialogImpression, params);
        }
    }
}
